import { request } from './http'

export interface Document {
  id: string
  title: string
  content: string
  user_id: string | null
  is_shared: boolean
  is_readonly: boolean
  is_presentation: boolean
  created_at: string
  updated_at: string
  is_owner: boolean
  can_edit: boolean
  user?: {
    id: string
    name: string
    email: string
  }
}

export interface ShareSettings {
  enabled: boolean
  is_readonly: boolean
  is_presentation?: boolean
}

export interface DocumentVersion {
  id: number
  document_id: string
  title: string
  content: string
  user_id: string | null
  version: number
  created_at: string
  updated_at: string
}

export interface PaginationMeta {
  current_page: number
  last_page: number
  total: number
  per_page: number
}

export interface PaginationLinks {
  first: string | null
  last: string | null
  prev: string | null
  next: string | null
}

export interface DocumentListResponse {
  data: Document[]
  meta: PaginationMeta
  links: PaginationLinks
}

export const documentService = {
  async list(page = 1): Promise<DocumentListResponse> {
    const { data } = await request<DocumentListResponse>(
      `/v1/documents${page > 1 ? `?page=${page}` : ''}`,
    )
    return data!
  },

  async get(id: string): Promise<Document> {
    const { data } = await request<{ data: Document }>(`/v1/documents/${id}`, {
      withSessionId: true,
    })
    return data!.data
  },

  async getForPresentation(id: string): Promise<Document> {
    const { data } = await request<{ data: Document }>(`/v1/documents/${id}/presentation`, {
      withSessionId: true,
    })
    return data!.data
  },

  async create(): Promise<Document> {
    const { data } = await request<{ data: Document }>('/v1/documents', {
      method: 'POST',
      withSessionId: true,
    })
    return data!.data
  },

  async update(id: string, payload: Partial<Document>): Promise<Document> {
    const { data } = await request<Document>(`/v1/documents/${id}`, {
      method: 'PUT',
      body: payload,
      withSessionId: true,
    })
    return data!
  },

  async remove(id: string): Promise<void> {
    await request(`/v1/documents/${id}`, { method: 'DELETE' })
  },

  async share(id: string, settings: ShareSettings): Promise<Document> {
    const { data } = await request<Document>(`/v1/documents/${id}/share`, {
      method: 'PUT',
      body: settings,
      withSessionId: true,
    })
    return data!
  },

  async versions(id: string): Promise<DocumentVersion[]> {
    const { data } = await request<DocumentVersion[]>(`/v1/documents/${id}/versions`)
    return data ?? []
  },

  async restoreVersion(id: string, versionId: number): Promise<Document> {
    const { data } = await request<Document>(
      `/v1/documents/${id}/versions/${versionId}/restore`,
      { method: 'POST' },
    )
    return data!
  },
}
